import type { AppLocale } from "../i18n/format";
import { ROUTES, type RouteName } from "../router";

/**
 * The app's two destinations.
 *
 * PLACEMENT: a bottom bar on phones, a slim top bar from 768px up.
 *
 * Why a top bar rather than a side rail on desktop: the content is a single
 * ~34rem column centred in the viewport. A rail would park a tall band of
 * chrome beside it and pull the eye sideways, away from the number — and with
 * exactly two destinations there is nothing to fill that band with. A top bar
 * costs one row of height, keeps the column centred, and leaves the number the
 * most prominent thing on screen, which is the whole point of the design.
 *
 * On phones the bar stays at the bottom, within thumb reach, and clears the
 * home indicator via env(safe-area-inset-bottom).
 */

interface Props {
  readonly route: RouteName;
  readonly locale: AppLocale;
  readonly onNavigate: (next: RouteName) => void;
}

const LABELS: Record<RouteName, { en: string; ar: string }> = {
  convert: { en: "Convert", ar: "تحويل" },
  themes: { en: "Themes", ar: "ثيمات" },
};

function ConvertGlyph() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 7h11l-3-3" />
      <path d="M17 13H6l3 3" />
    </svg>
  );
}

function ThemesGlyph() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2.75" y="2.75" width="6" height="6" rx="1" />
      <rect x="11.25" y="2.75" width="6" height="6" rx="1" />
      <rect x="2.75" y="11.25" width="6" height="6" rx="1" />
      <rect x="11.25" y="11.25" width="6" height="6" rx="1" />
    </svg>
  );
}

export function NavBar({ route, locale, onNavigate }: Props) {
  const items: Array<{ name: RouteName; glyph: React.ReactNode }> = [
    { name: "convert", glyph: <ConvertGlyph /> },
    { name: "themes", glyph: <ThemesGlyph /> },
  ];

  return (
    <nav className="nav" aria-label={locale === "ar" ? "التنقل الرئيسي" : "Main"}>
      <ul className="nav__list">
        {items.map((item) => {
          const active = route === item.name;
          return (
            <li key={item.name} className="nav__item">
              <a
                className="nav__link"
                href={ROUTES[item.name]}
                aria-current={active ? "page" : undefined}
                onClick={(event) => {
                  // Let modified clicks (new tab, download) behave normally.
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
                    return;
                  }
                  event.preventDefault();
                  onNavigate(item.name);
                }}
              >
                {item.glyph}
                <span className="nav__label">{LABELS[item.name][locale]}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
